import React, { useRef } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { contactInfo } from "./components/AppData";
import { useOnScreen } from "./hooks/useOnScreen";
import "./App.css";
import Hero from "./components/Hero";

const proofPoints = [
  "Mobile-first inspections",
  "Live issue ownership",
  "Client-ready handover reports",
];

const workflow = [
  {
    step: "01",
    title: "Capture",
    description: "Create a snag from the field with photos, location, priority, and notes in seconds.",
  },
  {
    step: "02",
    title: "Assign",
    description: "Route work to the right owner with due dates, status, and project-level visibility.",
  },
  {
    step: "03",
    title: "Resolve",
    description: "Review evidence, approve fixes, and keep every stakeholder aligned on progress.",
  },
  {
    step: "04",
    title: "Report",
    description: "Generate clean handover records with owners, history, photos, and remaining actions.",
  },
];

const featureModules = [
  {
    label: "Field capture",
    title: "Inspect faster from mobile",
    description: "Log snags with photos, notes, locations, and priority from the site floor before context is lost.",
  },
  {
    label: "Ownership",
    title: "Make every action accountable",
    description: "Assign issues to teams, track status, and keep contractors clear on what must move next.",
  },
  {
    label: "Visibility",
    title: "See project quality in real time",
    description: "Give managers a live view of blockers, approvals, overdue work, and handover readiness.",
  },
  {
    label: "Reporting",
    title: "Create cleaner closeout packs",
    description: "Turn field activity into client-ready reports without rebuilding the story by hand.",
  },
];

const App: React.FC = () => {
  const workflowRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const reportingRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  const workflowVisible = useOnScreen(workflowRef as React.RefObject<HTMLElement>);
  const featuresVisible = useOnScreen(featuresRef as React.RefObject<HTMLElement>);
  const reportingVisible = useOnScreen(reportingRef as React.RefObject<HTMLElement>);
  const ctaVisible = useOnScreen(ctaRef as React.RefObject<HTMLElement>);
  const contactVisible = useOnScreen(contactRef as React.RefObject<HTMLElement>);

  return (
    <div className="App">
      <Header />
      <main>
        <Hero />

        <section className="proof-section" aria-label="Arqite proof points">
          <div className="section-inner proof-strip">
            {proofPoints.map((point) => (
              <div className="proof-pill" key={point}>
                <span></span>
                {point}
              </div>
            ))}
          </div>
        </section>

        <section
          id="workflow"
          ref={workflowRef}
          className={`workflow-section fade-up ${workflowVisible ? "visible" : ""}`}
        >
          <div className="section-inner">
            <div className="section-heading">
              <span className="eyebrow">Product workflow</span>
              <h2>From site walk to signed handover.</h2>
            </div>
            <div className="workflow-track">
              {workflow.map((item) => (
                <article className="workflow-step" key={item.step}>
                  <span>{item.step}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="features"
          ref={featuresRef}
          className={`features-section fade-up ${featuresVisible ? "visible" : ""}`}
        >
          <div className="section-inner feature-showcase">
            <div className="feature-lead">
              <span className="eyebrow">Feature modules</span>
              <h2>The tools your field and office teams actually need.</h2>
            </div>
            <div className="feature-modules">
              {featureModules.map((feature, index) => (
                <article className={`feature-module module-${index + 1}`} key={feature.title}>
                  <span>{feature.label}</span>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="reporting"
          ref={reportingRef}
          className={`visibility-section fade-up ${reportingVisible ? "visible" : ""}`}
        >
          <div className="section-inner visibility-layout">
            <div className="report-panel" aria-label="Reporting dashboard preview">
              <div className="report-header">
                <span>Handover readiness</span>
                <strong>Client Pack / Tower A</strong>
              </div>
              <div className="report-score">
                <strong>94%</strong>
                <p>Evidence verified across open and recently closed items.</p>
              </div>
              <div className="report-list">
                <span>42 photo records approved</span>
                <span>11 owner updates synced</span>
                <span>3 final reviews remaining</span>
              </div>
            </div>
            <div className="section-copy">
              <span className="eyebrow">Field-to-office visibility</span>
              <h2>Give every stakeholder the same view of quality.</h2>
              <p>
                Site teams capture the truth in the moment. Office teams get a live status layer for decisions,
                reporting, and client communication.
              </p>
              <a href="#contact" className="cta-btn secondary">Talk to Sales</a>
            </div>
          </div>
        </section>

        <section
          id="cta"
          ref={ctaRef}
          className={`cta-section fade-up ${ctaVisible ? "visible" : ""}`}
        >
          <div className="section-inner cta-panel">
            <div>
              <span className="eyebrow">Start managing site quality with Arqite</span>
              <h2>Bring your snagging, ownership, and handover reporting into one workspace.</h2>
            </div>
            <div className="cta-actions">
              <a className="cta-btn primary" href="https://app.arqite.com" target="_blank" rel="noopener noreferrer">
                Login
              </a>
              <a className="cta-btn secondary" href={`mailto:${contactInfo.salesEmail}`}>
                Book a Demo
              </a>
            </div>
          </div>
        </section>

        <section
          id="contact"
          ref={contactRef}
          className={`contact-section fade-up ${contactVisible ? "visible" : ""}`}
        >
          <div className="section-inner contact-layout">
            <div className="section-copy">
              <span className="eyebrow">Contact</span>
              <h2>Ready to modernize your field operations?</h2>
              <p>
                Tell us how your team currently manages defects, inspections, and handover reporting. We will help
                you map the cleanest path into Arqite.
              </p>
            </div>
            <div className="contact-panel">
              <a href={`mailto:${contactInfo.salesEmail}`}>
                <span>Sales</span>
                {contactInfo.salesEmail}
              </a>
              <a href={`mailto:${contactInfo.supportEmail}`}>
                <span>Support</span>
                {contactInfo.supportEmail}
              </a>
              <div>
                <span>Hours</span>
                {contactInfo.hours}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default App;
